// src/pages/GameHub.tsx

import React from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonButton,
  IonIcon,
  IonBadge
} from '@ionic/react';
import { gameController, home, hourglass, compass as compassIcon, shield, flash } from 'ionicons/icons';
import { useProfile } from '../contexts/ProfileContext';
import { useBetaTester } from '../hooks/useBetaTester';
import './GameHub.css';

const GameHub: React.FC = () => {
  const { profile } = useProfile();
  const { isBetaTester } = useBetaTester();
  const isAdmin = profile?.is_admin || false;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>🎮 Game Hub</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div className="game-hub-container">
          <div className="hub-header">
            <h1>Welcome to the Game Hub</h1>
            <p>Choose your adventure or challenge your mind with our collection of games</p>
          </div>

          <div className="games-grid">
            {/* Dice Match Card - FIRST! */}
            <IonCard className="game-card featured">
              <div className="game-card-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                🎲
              </div>
              <IonCardHeader>
                <IonCardTitle>
                  Dice Match
                </IonCardTitle>
                <IonCardSubtitle>Merge Puzzle Game</IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <p className="game-description">
                  Merge matching dice to upgrade them from d4 all the way to d100.
                  Create sparkles, earn upgrades, and climb the leaderboard. How high can you go?
                </p>
                <div className="game-features">
                  <IonBadge color="primary">Solo</IonBadge>
                  <IonBadge color="secondary">Puzzle</IonBadge>
                  <IonBadge color="tertiary">Strategy</IonBadge>
                  <IonBadge color="success">Leaderboard</IonBadge>
                </div>
                <IonButton
                  expand="block"
                  color="primary"
                  routerLink="/games/dice-match/menu"
                  style={{ marginTop: '16px' }}
                >
                  <IonIcon icon={gameController} slot="start" />
                  Play Dice Match
                </IonButton>
              </IonCardContent>
            </IonCard>

            {/* Pip's Word Puzzle Card */}
            <IonCard className="game-card featured">
              <div className="game-card-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                🎮
              </div>
              <IonCardHeader>
                <IonCardTitle>
                  Pip's Word Puzzle
                </IonCardTitle>
                <IonCardSubtitle>Daily Word Puzzle</IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <p className="game-description">
                  Guess the gaming word in 6 tries! A Wordle-style puzzle game for gamers with daily challenges,
                  progressive hints, and points to earn. New puzzle every day!
                </p>
                <div className="game-features">
                  <IonBadge color="primary">Daily Puzzles</IonBadge>
                  <IonBadge color="secondary">Solo</IonBadge>
                  <IonBadge color="tertiary">Word Game</IonBadge>
                  <IonBadge color="success">Hints</IonBadge>
                </div>
                <IonButton
                  expand="block"
                  color="primary"
                  routerLink="/games/pips-word-puzzle"
                  style={{ marginTop: '16px' }}
                >
                  <IonIcon icon={gameController} slot="start" />
                  Play Pip's Word Puzzle
                </IonButton>
              </IonCardContent>
            </IonCard>

            {/* Oldschool Dungeon Escape Card */}
            <IonCard className="game-card featured">
              <div className="game-card-icon game-icon-gradient" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <IonIcon icon={compassIcon} style={{ fontSize: '48px' }} />
              </div>
              <IonCardHeader>
                <IonCardTitle>
                  Oldschool Dungeon Escape
                </IonCardTitle>
                <IonCardSubtitle>Text Adventure</IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <p className="game-description">
                  Explore mysterious dungeons, solve intricate puzzles, and uncover ancient secrets in this
                  classic text-based adventure. Features a sliding block puzzle, map system, and progressive hints! Completely Free to Play!
                </p>
                <div className="game-features">
                  <IonBadge color="primary">Puzzles</IonBadge>
                  <IonBadge color="secondary">Solo</IonBadge>
                  <IonBadge color="tertiary">Adventure</IonBadge>
                  <IonBadge color="success">Save/Load</IonBadge>
                </div>
                <IonButton
                  expand="block"
                  color="primary"
                  routerLink="/games/oldschool-dungeon-escape"
                  style={{ marginTop: '16px' }}
                >
                  <IonIcon icon={compassIcon} slot="start" />
                  Start Adventure
                </IonButton>
              </IonCardContent>
            </IonCard>

            {/* Pip's Creature Keeper Card */}
            <IonCard className="game-card featured">
              <div className="game-card-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                🛖
              </div>
              <IonCardHeader>
                <IonCardTitle>
                  Pip's Creature Keeper
                </IonCardTitle>
                <IonCardSubtitle>Creature Collection Adventure</IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <p className="game-description">
                  Restore a forgotten sanctuary by discovering and befriending 50 unique creatures.
                  Build habitats, manage happiness, and complete your collection! All features available with free account, plus members get unlimited catch attempts.
                </p>
                <div className="game-features">
                  <IonBadge color="success">50 Creatures</IonBadge>
                  <IonBadge color="secondary">Solo</IonBadge>
                  <IonBadge color="primary">Strategy</IonBadge>
                  <IonBadge color="success">Collection</IonBadge>
                </div>
                <IonButton
                  expand="block"
                  color="primary"
                  routerLink="/tabs/games/pips-creature-keeper"
                  style={{ marginTop: '16px' }}
                >
                  <IonIcon icon={home} slot="start" />
                  Start Creature Keeping
                </IonButton>
              </IonCardContent>
            </IonCard>

            {/* Pip's Guild Quest Card */}
            <IonCard className="game-card featured">
              <div className="game-card-icon" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
                ⚔️
              </div>
              <IonCardHeader>
                <IonCardTitle>
                  Pip's Guild Quest
                  <IonBadge color="warning" style={{ marginLeft: '8px', fontSize: '13px' }}>WIP</IonBadge>
                </IonCardTitle>
                <IonCardSubtitle>Cooperative Story Adventure</IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <p className="game-description">
                  Embark on cooperative story-driven adventures with your party. Make choices together,
                  face dangers in turn-based combat, and discover multiple endings based on your decisions.
                  Play asynchronously with friends!
                </p>
                <div className="game-features">
                  <IonBadge color="primary">1-5 Players</IonBadge>
                  <IonBadge color="secondary">Co-op</IonBadge>
                  <IonBadge color="tertiary">Story</IonBadge>
                  <IonBadge color="success">Turn-based</IonBadge>
                </div>
                <IonButton
                  expand="block"
                  color="primary"
                  routerLink="/games/pips-guild-quest"
                  style={{ marginTop: '16px' }}
                >
                  <IonIcon icon={gameController} slot="start" />
                  Start Quest
                </IonButton>
              </IonCardContent>
            </IonCard>

            {/* Crossroads TCG Card - Beta Testers Only */}
            {isBetaTester && (
              <IonCard className="game-card featured">
                <div className="game-card-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <IonIcon icon={flash} style={{ fontSize: '48px' }} />
                </div>
                <IonCardHeader>
                  <IonCardTitle>
                    Crossroads TCG
                    <IonBadge color="warning" style={{ marginLeft: '8px', fontSize: '13px' }}>BETA</IonBadge>
                  </IonCardTitle>
                  <IonCardSubtitle>Trading Card Game</IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <p className="game-description">
                    Choose Your Path. Shape the Story. A strategic trading card game where you command powerful
                    Game Masters and battle using elemental forces. Features unique commanders, creature summoning,
                    art spells, and dynamic environment effects!
                  </p>
                  <div className="game-features">
                    <IonBadge color="primary">1v1</IonBadge>
                    <IonBadge color="secondary">Strategy</IonBadge>
                    <IonBadge color="tertiary">TCG</IonBadge>
                    <IonBadge color="success">Deck Building</IonBadge>
                  </div>
                  <IonButton
                    expand="block"
                    color="primary"
                    routerLink="/games/crossroads"
                    style={{ marginTop: '16px' }}
                  >
                    <IonIcon icon={flash} slot="start" />
                    Play Crossroads
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}

            {/* More Coming Soon Card */}
            <IonCard className="game-card coming-soon">
              <div className="game-card-icon game-icon-gradient" style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }}>
                <IonIcon icon={hourglass} style={{ fontSize: '48px' }} />
              </div>
              <IonCardHeader>
                <IonCardTitle>More Coming Soon</IonCardTitle>
                <IonCardSubtitle>Stay Tuned!</IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <p className="game-description">
                  We're working on exciting new games and experiences. Check back soon to see what's next!
                </p>
                <div className="game-features">
                  <IonBadge color="medium">In Development</IonBadge>
                </div>
                <IonButton
                  expand="block"
                  disabled
                  style={{ marginTop: '16px' }}
                >
                  Coming Soon
                </IonButton>
              </IonCardContent>
            </IonCard>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default GameHub;